import React, { useEffect, useLayoutEffect, useRef } from 'react';
import styles from './index.module.scss';

type Direction = -1 | 1;
interface Props {
    workKey: string;
    axis?: 'vertical' | 'horizontal';
    previous?: React.ReactNode;
    next?: React.ReactNode;
    disabled?: boolean;
    onPrepare: (direction: Direction) => Promise<boolean>;
    onCommit: (direction: Direction) => void;
    children: React.ReactNode;
}

/** 只保留前后相邻画面；拖动直接更新合成层，不逐帧触发页面重新渲染。 */
const PhotographyDetailPager: React.FC<Props> = ({ workKey, axis = 'vertical', previous, next, disabled, onPrepare, onCommit, children }) => {
    const viewport = useRef<HTMLDivElement>(null);
    const track = useRef<HTMLDivElement>(null);
    const latest = useRef({ previous, next, disabled, onPrepare, onCommit });
    latest.current = { previous, next, disabled, onPrepare, onCommit };
    const gesture = useRef<{ id: number; x: number; y: number; time: number; size: number; active: boolean } | null>(null);
    const offset = useRef(0);
    const frame = useRef(0);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const finish = useRef<(() => void) | null>(null);
    const sequence = useRef(0);
    const settling = useRef(false);
    const suppressClick = useRef(0);

    const horizontal = axis === 'horizontal';
    const transform = (value: number) => horizontal ? `translate3d(${value}px, 0, 0)` : `translate3d(0, ${value}px, 0)`;
    const viewportSize = (element: HTMLDivElement | null) => horizontal
        ? element?.getBoundingClientRect().width || window.innerWidth
        : element?.getBoundingClientRect().height || window.innerHeight;

    const clearAnimation = () => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = null;
        finish.current = null;
        if (frame.current) cancelAnimationFrame(frame.current);
        frame.current = 0;
    };
    const reset = () => {
        sequence.current++;
        clearAnimation();
        gesture.current = null;
        settling.current = false;
        offset.current = 0;
        if (track.current) { track.current.style.transition = 'none'; track.current.style.transform = transform(0); }
    };
    useLayoutEffect(() => { reset(); return reset; }, [workKey, axis]); // eslint-disable-line react-hooks/exhaustive-deps
    useLayoutEffect(() => { if (disabled) reset(); }, [disabled]); // eslint-disable-line react-hooks/exhaustive-deps

    const move = (value: number) => {
        offset.current = value;
        if (frame.current) return;
        frame.current = requestAnimationFrame(() => {
            frame.current = 0;
            if (track.current) track.current.style.transform = transform(offset.current);
        });
    };
    const animate = (target: number, done?: () => void) => {
        clearAnimation();
        const node = track.current;
        if (!node) return;
        node.style.transform = transform(offset.current);
        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        const size = viewportSize(viewport.current);
        const duration = reducedMotion ? 0 : Math.max(140, Math.min(240, Math.abs(target - offset.current) / size * 260));
        settling.current = true;
        const complete = () => {
            clearAnimation();
            if (done) done();
            else { settling.current = false; offset.current = 0; node.style.transition = 'none'; node.style.transform = transform(0); }
        };
        finish.current = complete;
        // 将松手前的位置先提交，下一帧再吸附到完整一屏。
        frame.current = requestAnimationFrame(() => {
            frame.current = 0;
            node.style.transition = `transform ${duration}ms cubic-bezier(.22,.68,0,1)`;
            node.style.transform = transform(target);
            timer.current = setTimeout(complete, duration + 50);
        });
    };
    const release = async (direction: Direction, size: number) => {
        const token = ++sequence.current;
        settling.current = true;
        try {
            const ready = await latest.current.onPrepare(direction);
            if (token !== sequence.current || latest.current.disabled) return;
            if (!ready) { animate(0); return; }
            animate(-direction * size, () => {
                if (token === sequence.current && !latest.current.disabled) latest.current.onCommit(direction);
            });
        } catch {
            if (token === sequence.current) animate(0);
        }
    };

    return <div ref={viewport} className={`${styles.viewport} ${horizontal ? styles.horizontal : ''}`}
        onPointerDown={event => {
            suppressClick.current = 0;
            const target = event.target as Element;
            if (!event.currentTarget.contains(target)) return;
            if (target.closest('button,input,a,[role="button"],[data-photography-actions]')) { reset(); return; }
            if (latest.current.disabled || settling.current || event.isPrimary === false || event.button > 0) return;
            const size = viewportSize(event.currentTarget);
            gesture.current = { id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now(), size, active: false };
            if (track.current) track.current.style.transition = 'none';
        }}
        onPointerMove={event => {
            const start = gesture.current;
            if (!start || start.id !== event.pointerId) return;
            const dy = event.clientY - start.y;
            const dx = event.clientX - start.x;
            const delta = horizontal ? dx : dy;
            const crossDelta = horizontal ? dy : dx;
            if (!start.active && Math.abs(crossDelta) > 12 && Math.abs(crossDelta) > Math.abs(delta)) { gesture.current = null; return; }
            if (!start.active && (Math.abs(delta) < 8 || Math.abs(delta) <= Math.abs(crossDelta) * 1.2)) return;
            start.active = true;
            event.currentTarget.setPointerCapture?.(event.pointerId);
            if (event.cancelable) event.preventDefault();
            const neighbor = delta < 0 ? latest.current.next : latest.current.previous;
            move(Math.max(-start.size, Math.min(start.size, neighbor ? delta : delta * .24)));
        }}
        onPointerUp={event => {
            const start = gesture.current;
            gesture.current = null;
            if (!start || start.id !== event.pointerId || !start.active) return;
            suppressClick.current = Date.now() + 400;
            const delta = horizontal ? event.clientX - start.x : event.clientY - start.y;
            const elapsed = Math.max(1, performance.now() - start.time);
            const threshold = Math.min(120, Math.max(50, start.size * .13));
            if (Math.abs(delta) >= threshold || (Math.abs(delta) >= 40 && Math.abs(delta) / elapsed >= .45)) void release(delta < 0 ? 1 : -1, start.size);
            else animate(0);
        }}
        onPointerCancel={() => { if (gesture.current?.active) animate(0); gesture.current = null; }}
        onDragStart={event => event.preventDefault()}
        onClickCapture={event => {
            if ((event.target as Element).closest('button,input,a,[role="button"],[data-photography-actions]')) { reset(); return; }
            if (Date.now() < suppressClick.current) { event.preventDefault(); event.stopPropagation(); }
        }}>
        <div ref={track} className={styles.track} data-testid="photography-detail-track"
            onTransitionEnd={event => { if (event.target === track.current && event.propertyName === 'transform') finish.current?.(); }}>
            <div className={styles.previous} aria-hidden="true" inert>{previous}</div>
            <div className={styles.current} data-photography-current="true">{children}</div>
            <div className={styles.next} aria-hidden="true" inert>{next}</div>
        </div>
    </div>;
};
export default PhotographyDetailPager;

/** 详情浏览时保持背景列表的 React 子树，分页数据在关闭弹窗后再更新到瀑布流。 */
export const PhotographyWorkListBackdrop = React.memo<{ frozen: boolean; children: React.ReactNode }>(
    ({ children }) => <>{children}</>,
    (previous, next) => previous.frozen && next.frozen,
);

/** 仅预取下一件视频，不自动播放；离开作品弹窗时释放请求和媒体资源。 */
export const PhotographyVideoWarmup: React.FC<{ src: string }> = ({ src }) => {
    const ref = useRef<HTMLVideoElement>(null);
    useEffect(() => {
        const video = ref.current;
        if (!video) return;
        video.src = src;
        return () => { video.pause(); video.removeAttribute('src'); video.load(); };
    }, [src]);
    return <video ref={ref} data-photography-preload="true" aria-hidden="true" muted playsInline preload="auto" className={styles.warmup} />;
};
